import React, { useState, useEffect, useRef } from 'react';
import { UserProfileData, Skill, Occupation, SkillExperience, OccupationExperience } from '../types';
import SkillSelector from './SkillSelector';
import OccupationSelector from './OccupationSelector';

interface ProfileRegistrationPageProps {
  userId: number;
}

// Touch-friendly grid buttons for selecting options
type OptionItem = { value: string | number; label: string };
const SelectionGrid: React.FC<{
  options: OptionItem[];
  value: string | number;
  selectedValues?: number[]; // 複数選択対応
  isMultiple?: boolean; // 複数選択モードかどうか
  onChange: (value: string | number) => void;
  columns?: number;
}> = ({ options, value, selectedValues, isMultiple = false, onChange, columns = 3 }) => {
  const isSelected = (optValue: string | number) => {
    if (isMultiple && selectedValues) {
      return selectedValues.includes(Number(optValue));
    }
    return value === optValue;
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: '0.5rem' }}>
      {options.map((opt) => (
        <button
          key={String(opt.value)}
          type="button"
          className={`btn ${isSelected(opt.value) ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
};

// Question type definitions
type QuestionType = 'selection' | 'input' | 'name_group' | 'skills' | 'occupations';

interface Question {
  id: string;
  type: QuestionType;
  title: string;
  field?: keyof UserProfileData;
  fields?: Array<keyof UserProfileData>; // For name_group
  required?: boolean;
  options?: OptionItem[];
  columns?: number;
  inputType?: 'text' | 'tel' | 'date' | 'number';
  placeholder?: string;
  autoAdvance?: boolean; // Auto advance on selection
}

const ProfileRegistrationPage: React.FC<ProfileRegistrationPageProps> = ({ userId }) => {
  const [pendingJob, setPendingJob] = useState<{ job_listing_id: number | null; job_title: string; job_source: string; company_name?: string } | null>(null);
  const [profileData, setProfileData] = useState<UserProfileData>({
    first_name: '',
    last_name: '',
    first_name_kana: '',
    last_name_kana: '',
    phone_number: '',
    birth_date: '',
    gender: '',
    prefecture: '',
    desired_work_days: 1,
    desired_work_days_list: [],
    current_status: '',
    availability: '',
    commute_time_preference: ''
  });
  
  const [selectedSkills, setSelectedSkills] = useState<SkillExperience[]>([]);
  const [selectedOccupations, setSelectedOccupations] = useState<OccupationExperience[]>([]);
  const [errors, setErrors] = useState<{[key: string]: string[]}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Current question index (0-based)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  
  // Question flow: selection-based first, then input-required
  const questions: Question[] = [
    // Selection-based questions first (auto-advance)
    {
      id: 'desired_work_days',
      type: 'selection',
      title: '希望稼働日数を教えてください。',
      field: 'desired_work_days',
      options: [1,2,3,4,5].map((d) => ({ value: d, label: `週${d}日` })),
      columns: 5,
      autoAdvance: true
    },
    {
      id: 'current_status',
      type: 'selection',
      title: '現在の状況',
      field: 'current_status',
      required: true,
      options: [
        { value: 'full_time', label: '正社員' },
        { value: 'freelance', label: 'フリーランス' },
        { value: 'part_time', label: 'パートタイム' },
        { value: 'contract', label: '契約社員' },
        { value: 'student', label: '学生' }
      ],
      columns: 3,
      autoAdvance: true
    },
    {
      id: 'availability',
      type: 'selection',
      title: '案件に参画可能な時期を教えてください',
      field: 'availability',
      required: true,
      options: [
        { value: 'immediate', label: '即日' },
        { value: 'within_one_month', label: '1ヶ月以内' },
        { value: 'within_three_months', label: '3ヶ月以内' },
        { value: 'within_six_months', label: '6ヶ月以内' },
        { value: 'within_one_year', label: '1年以内' },
        { value: 'considering', label: '検討中' }
      ],
      columns: 3,
      autoAdvance: true
    },
    {
      id: 'commute_time_preference',
      type: 'selection',
      title: '希望通勤時間を教えてください',
      field: 'commute_time_preference',
      required: true,
      options: [
        { value: 'within_30_minutes', label: '30分以内' },
        { value: 'within_1_hour', label: '1時間以内' },
        { value: 'within_1_hour_30_minutes', label: '1時間30分以内' }
      ],
      columns: 2,
      autoAdvance: true
    },
    {
      id: 'gender',
      type: 'selection',
      title: '性別を教えてください',
      field: 'gender',
      required: true,
      options: [
        { value: 'male', label: '男性' },
        { value: 'female', label: '女性' },
        { value: 'undisclosed', label: '回答しない' }
      ],
      columns: 3,
      autoAdvance: true
    },
    {
      id: 'prefecture',
      type: 'selection',
      title: '現在お住まいの都道府県を教えてください',
      field: 'prefecture',
      required: true,
      options: [
    { value: 'hokkaido', label: '北海道' },
    { value: 'aomori', label: '青森県' },
    { value: 'iwate', label: '岩手県' },
    { value: 'miyagi', label: '宮城県' },
    { value: 'akita', label: '秋田県' },
    { value: 'yamagata', label: '山形県' },
    { value: 'fukushima', label: '福島県' },
    { value: 'ibaraki', label: '茨城県' },
    { value: 'tochigi', label: '栃木県' },
    { value: 'gunma', label: '群馬県' },
    { value: 'saitama', label: '埼玉県' },
    { value: 'chiba', label: '千葉県' },
    { value: 'tokyo', label: '東京都' },
    { value: 'kanagawa', label: '神奈川県' },
    { value: 'niigata', label: '新潟県' },
    { value: 'toyama', label: '富山県' },
    { value: 'ishikawa', label: '石川県' },
    { value: 'fukui', label: '福井県' },
    { value: 'yamanashi', label: '山梨県' },
    { value: 'nagano', label: '長野県' },
    { value: 'gifu', label: '岐阜県' },
    { value: 'shizuoka', label: '静岡県' },
    { value: 'aichi', label: '愛知県' },
    { value: 'mie', label: '三重県' },
    { value: 'shiga', label: '滋賀県' },
    { value: 'kyoto', label: '京都府' },
    { value: 'osaka', label: '大阪府' },
    { value: 'hyogo', label: '兵庫県' },
    { value: 'nara', label: '奈良県' },
    { value: 'wakayama', label: '和歌山県' },
    { value: 'tottori', label: '鳥取県' },
    { value: 'shimane', label: '島根県' },
    { value: 'okayama', label: '岡山県' },
    { value: 'hiroshima', label: '広島県' },
    { value: 'yamaguchi', label: '山口県' },
    { value: 'tokushima', label: '徳島県' },
    { value: 'kagawa', label: '香川県' },
    { value: 'ehime', label: '愛媛県' },
    { value: 'kochi', label: '高知県' },
    { value: 'fukuoka', label: '福岡県' },
    { value: 'saga', label: '佐賀県' },
    { value: 'nagasaki', label: '長崎県' },
    { value: 'kumamoto', label: '熊本県' },
    { value: 'oita', label: '大分県' },
    { value: 'miyazaki', label: '宮崎県' },
    { value: 'kagoshima', label: '鹿児島県' },
    { value: 'okinawa', label: '沖縄県' }
      ],
      columns: 3,
      autoAdvance: true
    },
    // Input-required questions last
    {
      id: 'name_group',
      type: 'name_group',
      title: 'お名前を姓名/カナで教えてください',
      fields: ['last_name', 'first_name', 'last_name_kana', 'first_name_kana'],
      required: true
    },
    {
      id: 'phone_number',
      type: 'input',
      title: '電話番号を教えてください',
      field: 'phone_number',
      required: true,
      inputType: 'tel',
      placeholder: '09012345678'
    },
    {
      id: 'birth_date',
      type: 'input',
      title: '生年月日を教えてください',
      field: 'birth_date',
      required: true,
      inputType: 'date'
    },
    {
      id: 'skills',
      type: 'skills',
      title: 'スキル情報と経験年数を教えてください。複数ある場合は複数選択してください。'
    },
    {
      id: 'occupations',
      type: 'occupations',
      title: '職種情報と経験年数を教えてください。複数ある場合は複数選択してください。'
    },
    {
      id: 'current_annual_salary',
      type: 'input',
      title: '現在の年収(万円)',
      field: 'current_annual_salary',
      inputType: 'number',
      placeholder: '万円'
    }
  ];

  const totalQuestions = questions.length;
  const currentQuestion = questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;

  // Persist progress in localStorage
  const STORAGE_KEY = `profile_registration_${userId}_v3`;
  const RETURN_TO_KEY = 'registration_return_to_url';
  const GLOBAL_FLASH_KEY = 'global_flash_notice';
  const [isRestored, setIsRestored] = useState(false);

  useEffect(() => {
    try {
      // Clear old localStorage versions
      try {
        localStorage.removeItem(`profile_registration_${userId}_v1`);
        localStorage.removeItem(`profile_registration_${userId}_v2`);
      } catch(_) {}

      // Save return URL on first load
      if (!localStorage.getItem(RETURN_TO_KEY)) {
        const ref = document.referrer;
        try {
          if (ref) {
            const refUrl = new URL(ref);
            if (refUrl.origin === window.location.origin && refUrl.pathname !== window.location.pathname) {
              localStorage.setItem(RETURN_TO_KEY, refUrl.pathname + refUrl.search + refUrl.hash);
            }
          }
        } catch(_) {}
      }

      // Restore saved progress
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          if (parsed.profileData) setProfileData(parsed.profileData);
          if (Array.isArray(parsed.selectedSkills)) setSelectedSkills(parsed.selectedSkills);
          if (Array.isArray(parsed.selectedOccupations)) setSelectedOccupations(parsed.selectedOccupations);
          if (typeof parsed.currentQuestionIndex === 'number' && parsed.currentQuestionIndex < totalQuestions) {
            setCurrentQuestionIndex(parsed.currentQuestionIndex);
          }
        }
      }
      // Fetch pending job application info for display
      fetch('/api/v1/pending_job_application', { headers: { 'Accept': 'application/json' } })
        .then(async (res) => {
          if (!res.ok) return;
          const data = await res.json();
          if (data && data.pending_job_application) {
            setPendingJob(data.pending_job_application);
          }
        })
        .catch(() => {});
    } catch (e) {
      console.warn('Failed to load saved progress from localStorage', e);
    } finally {
      setIsRestored(true);
    }
  }, [userId]);

  useEffect(() => {
    if (!isRestored) return;
    try {
      const dataToSave = {
        profileData,
        selectedSkills,
        selectedOccupations,
        currentQuestionIndex,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    } catch (e) {
      console.warn('Failed to save progress to localStorage', e);
    }
  }, [profileData, selectedSkills, selectedOccupations, currentQuestionIndex, STORAGE_KEY, isRestored]);

  const clearFieldError = (field: keyof UserProfileData) => {
    if (errors[field as string]) {
      const next = { ...errors };
      delete next[field as string];
      setErrors(next);
    }
  };

  // 電話番号のバリデーション関数
  const validatePhoneNumber = (phoneNumber: string): boolean => {
    // 数字のみで10桁以上11桁以下
    const phoneRegex = /^\d{10,11}$/;
    return phoneRegex.test(phoneNumber);
  };

  // 電話番号のフォーマット関数（ハイフンなしで数字のみ）
  const formatPhoneNumber = (value: string): string => {
    return value.replace(/[^\d]/g, '');
  };

  const goToNextQuestion = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const goToPrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const isCurrentQuestionValid = (): boolean => {
    if (!currentQuestion) return false;

    switch (currentQuestion.type) {
      case 'name_group':
        return currentQuestion.fields!.every(field => {
          const value = (profileData as any)[field];
          return value !== null && value !== undefined && String(value).trim() !== '';
        });
      case 'input':
        if (currentQuestion.field) {
          const value = (profileData as any)[currentQuestion.field];
          if (!currentQuestion.required || (value !== null && value !== undefined && String(value).trim() !== '')) {
            // 電話番号の場合は追加バリデーション
            if (currentQuestion.field === 'phone_number') {
              return validatePhoneNumber(String(value).trim());
            }
            return true;
          }
          return false;
        }
        return true;
      case 'selection':
        if (currentQuestion.field) {
          const value = (profileData as any)[currentQuestion.field];
          return !currentQuestion.required || (value !== null && value !== undefined && String(value).trim() !== '');
        }
        return true;
      case 'skills':
      case 'occupations':
        return true; // Optional sections
      default:
        return true;
    }
  };

  const validateAndAdvance = () => {
    if (!isCurrentQuestionValid()) {
      if (currentQuestion.type === 'name_group') {
        const nextErrors: {[k: string]: string[]} = { ...errors };
        currentQuestion.fields!.forEach((field) => {
          const value = (profileData as any)[field];
          if (!value || String(value).trim() === '') {
            nextErrors[field as string] = ['必須項目です。'];
          } else {
            delete nextErrors[field as string];
          }
        });
        setErrors(nextErrors);
      } else if (currentQuestion.field) {
        setErrors(prev => ({ ...prev, [currentQuestion.field as string]: ['必須項目です。'] }));
      }
      return;
    }
    goToNextQuestion();
  };

  const handleSelectionChange = (value: string | number) => {
    if (currentQuestion.field) {
      const field = currentQuestion.field;
      if (field === 'desired_work_days') {
        // 複数選択対応: 配列で管理
        const currentList = profileData.desired_work_days_list || [];
        const numValue = Number(value);
        if (currentList.includes(numValue)) {
          // 既に選択されていれば削除
          setProfileData({ 
            ...profileData, 
            desired_work_days_list: currentList.filter(d => d !== numValue) 
          });
        } else {
          // 追加
          setProfileData({ 
            ...profileData, 
            desired_work_days_list: [...currentList, numValue].sort() 
          });
        }
      } else {
        setProfileData({ ...profileData, [field]: String(value) });
        // Auto advance if enabled for non-multi-select fields
        if (currentQuestion.autoAdvance) {
          setTimeout(goToNextQuestion, 200); // Small delay for visual feedback
        }
      }
      clearFieldError(field);
    }
  };

  const handleInputChange = (value: string) => {
    if (currentQuestion.field) {
      const field = currentQuestion.field;
      let processedValue = value;
      
      // 電話番号の場合は数字のみにフォーマット
      if (field === 'phone_number') {
        processedValue = formatPhoneNumber(value);
      }
      
      if (field === 'current_annual_salary') {
        const n = parseInt(processedValue, 10);
        setProfileData({ ...profileData, [field]: isNaN(n) ? undefined as any : (n as any) });
      } else {
        setProfileData({ ...profileData, [field]: processedValue });
      }
      
      // 電話番号の場合はリアルタイムバリデーション
      if (field === 'phone_number') {
        if (processedValue && !validatePhoneNumber(processedValue)) {
          setErrors({ ...errors, [field]: ['電話番号は10桁以上の数字で入力してください'] });
        } else {
          clearFieldError(field);
        }
      } else {
        clearFieldError(field);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 年収入力時以外の送信を防ぐ
    if (currentQuestion?.id !== 'current_annual_salary') {
      return;
    }
    // 入力未完了の送信を防ぐ
    if (!isCurrentQuestionValid()) {
      if (currentQuestion.field) {
        setErrors(prev => ({ ...prev, [currentQuestion.field as string]: ['必須項目です。'] }));
      }
      return;
    }
    
    setIsSubmitting(true);
    setErrors({});

    // フロントエンドバリデーション
    const validationErrors: {[key: string]: string[]} = {};
    
    // 電話番号のバリデーション
    if (profileData.phone_number && !validatePhoneNumber(profileData.phone_number)) {
      validationErrors.phone_number = ['電話番号は10桁以上の数字で入力してください'];
    }
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      if (!csrfToken) {
        throw new Error('CSRF token not found');
      }
      
      const response = await fetch('/api/v1/user_profiles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          user_profile: profileData,
          skill_experiences: selectedSkills,
          occupation_experiences: selectedOccupations
        })
      });

      if (response.ok) {
        const result = await response.json();
        try { localStorage.removeItem(STORAGE_KEY); } catch(_) {}
        try { localStorage.setItem(GLOBAL_FLASH_KEY, 'プロフィールの登録が完了しました。'); } catch(_) {}

        // Decide redirect target
        let target = '/';
        if (result && typeof result === 'object') {
          if (result.redirect_url) {
            target = result.redirect_url;
          } else if (result.applied) {
            target = '/mypage/applied_job_listings';
          } else {
            try {
              const stored = localStorage.getItem(RETURN_TO_KEY);
              if (stored && typeof stored === 'string') {
                target = stored;
              }
              localStorage.removeItem(RETURN_TO_KEY);
            } catch(_) {}
          }
        }

        const successDiv = document.createElement('div');
        successDiv.className = 'alert alert-success';
        successDiv.textContent = 'プロフィールが正常に保存されました。移動します...';
        document.querySelector('form')?.prepend(successDiv);
        setTimeout(() => { window.location.href = target; }, 600);
      } else {
        const errorData = await response.json();
        
        // Handle different error formats
        if (Array.isArray(errorData.errors)) {
          setErrors({ base: errorData.errors });
        } else if (typeof errorData.errors === 'object') {
          setErrors(errorData.errors);
        } else {
          setErrors({ base: ['プロフィールの保存に失敗しました。'] });
        }
      }
    } catch (error) {
      setErrors({ base: ['プロフィールの作成に失敗しました。'] });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderQuestion = () => {
    if (!currentQuestion) return null;

    switch (currentQuestion.type) {
      case 'selection':
  return (
          <div className="mb-4">
            <label className="form-label fs-5 mb-3">
              {currentQuestion.title}
              {currentQuestion.required && <span className="text-danger"> *</span>}
              {currentQuestion.field === 'desired_work_days' && (
                <div className="form-text text-muted mt-1">複数選択可能です</div>
              )}
            </label>
            <SelectionGrid
              options={currentQuestion.options!}
              value={(profileData as any)[currentQuestion.field!]}
              selectedValues={currentQuestion.field === 'desired_work_days' ? profileData.desired_work_days_list : undefined}
              isMultiple={currentQuestion.field === 'desired_work_days'}
              onChange={handleSelectionChange}
              columns={currentQuestion.columns || 3}
            />
            {errors[currentQuestion.field as string] && (
              <div className="text-danger mt-2">{errors[currentQuestion.field as string].join(', ')}</div>
            )}
        </div>
        );

      case 'input':
        return (
          <div className="mb-4">
            <label className="form-label fs-5 mb-3">
              {currentQuestion.title}
              {currentQuestion.required && <span className="text-danger"> *</span>}
            </label>
            <input
              type={currentQuestion.inputType || 'text'}
              className={`form-control form-control-lg ${errors[currentQuestion.field as string] ? 'is-invalid' : ''}`}
              value={(profileData as any)[currentQuestion.field!]}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // 年収入力時のみ送信、それ以外は次の質問へ
                  if (currentQuestion.field === 'current_annual_salary') {
                    handleSubmit(e as any);
                  } else {
                    validateAndAdvance();
                  }
                }
              }}
              placeholder={currentQuestion.placeholder}
              required={currentQuestion.required}
            />
            {currentQuestion.field === 'phone_number' && (
              <div className="form-text text-muted">
                ハイフンなしで10桁以上の数字で入力してください（例：09012345678）
              </div>
            )}
            {errors[currentQuestion.field as string] && (
              <div className="invalid-feedback">{errors[currentQuestion.field as string].join(', ')}</div>
            )}
        </div>
        );

      case 'name_group':
        return (
          <div className="mb-4">
            <label className="form-label fs-5 mb-3">
              {currentQuestion.title}
              {currentQuestion.required && <span className="text-danger"> *</span>}
            </label>
          <div className="row">
            <div className="col-md-6 mb-3">
                <label className="form-label">姓</label>
              <input
                type="text"
                className={`form-control ${errors.last_name ? 'is-invalid' : ''}`}
                value={profileData.last_name}
                  onChange={(e) => { setProfileData({ ...profileData, last_name: e.target.value }); clearFieldError('last_name'); }}
                required
              />
                {errors.last_name && <div className="invalid-feedback">{errors.last_name.join(', ')}</div>}
            </div>
            <div className="col-md-6 mb-3">
                <label className="form-label">名</label>
              <input
                type="text"
                className={`form-control ${errors.first_name ? 'is-invalid' : ''}`}
                value={profileData.first_name}
                  onChange={(e) => { setProfileData({ ...profileData, first_name: e.target.value }); clearFieldError('first_name'); }}
                required
              />
                {errors.first_name && <div className="invalid-feedback">{errors.first_name.join(', ')}</div>}
            </div>
            <div className="col-md-6 mb-3">
                <label className="form-label">姓（カナ）</label>
              <input
                type="text"
                className={`form-control ${errors.last_name_kana ? 'is-invalid' : ''}`}
                value={profileData.last_name_kana}
                  onChange={(e) => { setProfileData({ ...profileData, last_name_kana: e.target.value }); clearFieldError('last_name_kana'); }}
                required
              />
                {errors.last_name_kana && <div className="invalid-feedback">{errors.last_name_kana.join(', ')}</div>}
            </div>
            <div className="col-md-6 mb-3">
                <label className="form-label">名（カナ）</label>
              <input
                type="text"
                className={`form-control ${errors.first_name_kana ? 'is-invalid' : ''}`}
                value={profileData.first_name_kana}
                  onChange={(e) => { setProfileData({ ...profileData, first_name_kana: e.target.value }); clearFieldError('first_name_kana'); }}
                required
              />
                {errors.first_name_kana && <div className="invalid-feedback">{errors.first_name_kana.join(', ')}</div>}
                </div>
            </div>
          </div>
        );

      case 'skills':
        return (
          <div className="mb-4">
            <h5 className="mb-3">{currentQuestion.title}</h5>
          <SkillSelector
            experiences={selectedSkills}
            onChange={setSelectedSkills}
          />
        </div>
        );

      case 'occupations':
        return (
          <div className="mb-4">
            <h5 className="mb-3">{currentQuestion.title}</h5>
          <OccupationSelector
            experiences={selectedOccupations}
            onChange={setSelectedOccupations}
          />
        </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      minHeight: '100vh',
      padding: '0'
    }}>
      <div className="container-fluid p-0" style={{ minHeight: '100vh' }}>
        <div className="row justify-content-center align-items-center min-vh-100 p-3">
          <div className="col-12 col-md-10 col-lg-8 col-xl-7 col-xxl-6">
            <form onSubmit={(e) => {
              // 年収入力時のみフォーム送信を許可
              if (currentQuestion?.id === 'current_annual_salary') {
                handleSubmit(e);
              } else {
                e.preventDefault();
              }
            }}>
              {errors.base && (
                <div className="alert alert-danger mb-4 rounded-4 border-0">
                  {errors.base.map((error, index) => (
                    <div key={index}>{error}</div>
                  ))}
                </div>
              )}

              {pendingJob && (
                <div className="alert alert-info border-0 mb-4 rounded-4" style={{ backgroundColor: '#e3f2fd' }}>
                  <div className="d-flex align-items-start">
                    <i className="bi bi-info-circle-fill text-primary me-2 mt-1"></i>
                    <div>
                      <h6 className="alert-heading mb-2 text-primary">応募する案件</h6>
                      <p className="mb-1 fw-semibold">{pendingJob.job_title}</p>
                      <p className="mb-0 small text-muted">
                        提供元：{pendingJob.job_source}
                        {pendingJob.company_name && (<><br/>会社名：{pendingJob.company_name}</>)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div 
                className="bg-white border-0 rounded-4 overflow-hidden"
                style={{ 
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1), 0 10px 20px rgba(0,0,0,0.05)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <div className="p-5" style={{ minHeight: '400px' }}>
                  <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div 
                        className="badge text-dark px-3 py-2 rounded-pill"
                        style={{ 
                          backgroundColor: '#f8f9fa',
                          fontSize: '0.875rem',
                          fontWeight: '500'
                        }}
                      >
                        質問 {currentQuestionIndex + 1} / {totalQuestions}
                      </div>
                      <div 
                        className="progress"
                        style={{ width: '120px', height: '6px' }}
                      >
                        <div 
                          className="progress-bar bg-primary rounded-pill"
                          style={{ 
                            width: `${((currentQuestionIndex + 1) / totalQuestions) * 100}%`,
                            transition: 'width 0.3s ease'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ minHeight: '280px' }}>
                    {renderQuestion()}
                </div>

                  <div className="d-flex justify-content-between align-items-center mt-5">
                    <button 
                      type="button" 
                      className="btn btn-light px-4 py-3 rounded-3 border-0"
                      style={{
                        backgroundColor: '#f8f9fa',
                        color: '#6c757d',
                        fontWeight: '500',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={goToPrevQuestion}
                      disabled={currentQuestionIndex === 0}
                      onMouseEnter={(e) => {
                        if (!e.currentTarget.disabled) {
                          e.currentTarget.style.backgroundColor = '#e9ecef';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      ← 戻る
                    </button>
                    
                    {(currentQuestion?.id === 'current_annual_salary') ? (
                      <button
                        type="submit"
                        className="btn btn-primary px-5 py-3 rounded-3 border-0 fw-bold"
                        style={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          fontSize: '1.1rem',
                          boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                          transition: 'all 0.2s ease'
                        }}
                        disabled={isSubmitting || !isCurrentQuestionValid()}
                        onMouseEnter={(e) => {
                          if (!e.currentTarget.disabled) {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.3)';
                        }}
                      >
                        {isSubmitting ? '登録中...' : '🎉 プロフィールを登録'}
                      </button>
                    ) : (
                      <button 
                        type="button" 
                        className="btn btn-primary px-4 py-3 rounded-3 border-0 fw-semibold"
                        style={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={validateAndAdvance}
                        disabled={currentQuestion.autoAdvance && !isCurrentQuestionValid()}
                        onMouseEnter={(e) => {
                          if (!e.currentTarget.disabled) {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.3)';
                        }}
                      >
                        次へ →
                      </button>
              )}
            </div>
                </div>
            </div>
            </form>
          </div>
        </div>
      </div>
      </div>
  );
};

export default ProfileRegistrationPage;
